import merge from 'lodash/merge';
import uniq from 'lodash/uniq';
import { createAction, createReducer } from 'redux-act';
import { App } from 'src/types';

import {
  CommentsTotal,
  IComments,
  ICommentsDictionary,
  ICommentsState,
  TComment,
  TCommentsListQuery,
  TDataForUpdateCreateComment,
} from '../interfaces/comments';
import {
  deleteComment,
  getComments,
  getCommentsTotal,
  getCommentsTotalForList,
  postComment,
  updateComment,
} from '../services/comments';
import { deleteLike, postLike } from '../services/likes';

export interface IState {
  [articleId: number]: ICommentsState;
}

const initialState: IState = {};

export const commentsRequestStart = createAction<number>('comments/load/start');
export const commentsRequestFailure = createAction<{ articleId: number; errorMessage: string }>(
  'comments/load/failure',
);

export const loadingCommentsTotalSuccess = createAction<{ total: number; articleId: number }>(
  'comments/load/total/success',
);

export const loadingCommentTotalsForListSuccess = createAction<{ totals: CommentsTotal[] }>(
  'comments/load/total/list/success',
);

export const loadingCommentsTotalsForListFailure = createAction<{ articleIds: number[]; errorMessage: string }>(
  'comments/load/total/list/failure',
);

export const loadingFirstLevelCommentsSuccess = createAction<{ comments: IComments; articleId: number }>(
  'comments/load/success',
);
export const loadingChildCommentsSuccess = createAction<{
  childComments: IComments;
  articleId: number;
  parentId: number;
}>('comments/children/load/success');
export const loadingCommentSuccess = createAction<{
  comment: TComment;
  parentId: number | null;
  articleId: number;
  afterEditing?: boolean;
}>('comment/load/success');

export const commentRemoveFirstLevelSuccess = createAction<{
  commentId: number;
  articleId: number;
}>('comment/remove/success');

export const commentRemoveChildSuccess = createAction<{
  commentId: number;
  parentId: number;
  articleId: number;
}>('comment/remove/success');

export const updateCommentLike = createAction<{ commentId: number; articleId: number; isLiked: boolean }>(
  'comment/like/update',
);

export const fetchCommentsTotal = (articleId): App.ReduxAction => {
  return async (dispatch) => {
    try {
      const total = await getCommentsTotal(articleId);
      dispatch(loadingCommentsTotalSuccess({ total, articleId }));
    } catch (e) {
      const errorMessage = e?.message ?? null;
      dispatch(commentsRequestFailure({ articleId, errorMessage }));
    }
  };
};

export const fetchCommentsTotalForList = (articleIds: number[]): App.ReduxAction => {
  return async (dispatch) => {
    try {
      const totals = await getCommentsTotalForList(articleIds);
      dispatch(loadingCommentTotalsForListSuccess({ totals }));
    } catch (e) {
      const errorMessage = e?.message ?? null;
      dispatch(loadingCommentsTotalsForListFailure({ articleIds, errorMessage }));
    }
  };
};

export const toggleCommentLike = (commentId, articleId, isLiked, fingerPrint?: string): App.ReduxAction => {
  return async (dispatch, getState) => {
    const state = getState();
    const articleComments = state[articleId];

    if (!articleComments?.loading) {
      try {
        dispatch(commentsRequestStart(articleId));
        if (isLiked) {
          await deleteLike({ postId: articleId, entityId: commentId, fingerPrint });
        } else {
          await postLike({ postId: articleId, entityId: commentId, fingerPrint });
        }
        dispatch(updateCommentLike({ commentId, articleId, isLiked }));
      } catch (e) {
        const errorMessage = e?.message ?? null;
        dispatch(commentsRequestFailure({ articleId, errorMessage }));
      }
    }
  };
};

export const fetchFirstLevelComments = (query: TCommentsListQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const state = getState();
    const articleId = query.PostId;
    const articleComments = state[articleId];

    if (!articleComments?.loading) {
      try {
        dispatch(commentsRequestStart(articleId));
        const comments = await getComments(query);

        dispatch(loadingFirstLevelCommentsSuccess({ comments, articleId }));
      } catch (e) {
        const errorMessage = e?.message ?? null;
        dispatch(commentsRequestFailure({ articleId, errorMessage }));
      }
    }
  };
};

export const fetchChildComments = (query: TCommentsListQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const state = getState();
    const articleId = query.PostId;
    const parentId = query.ParentId;
    const articleComments = state[articleId];

    if (!articleComments?.loading) {
      try {
        dispatch(commentsRequestStart(articleId));
        const childComments = await getComments(query);
        dispatch(loadingChildCommentsSuccess({ childComments, articleId, parentId }));
      } catch (e) {
        const errorMessage = e?.message ?? null;
        dispatch(commentsRequestFailure({ articleId, errorMessage }));
      }
    }
  };
};

export const createComment = (commentData: TDataForUpdateCreateComment): App.ReduxAction => {
  return async (dispatch, getState) => {
    const state = getState();
    const articleId = commentData.postId;
    const parentId: number | null = commentData.parentId;
    const articleComments = state[articleId];

    if (!articleComments?.loading) {
      try {
        dispatch(commentsRequestStart(articleId));
        const comment = await postComment(commentData);
        dispatch(loadingCommentSuccess({ comment, parentId, articleId }));
      } catch (e) {
        const errorMessage = e?.message ?? null;
        dispatch(commentsRequestFailure({ articleId, errorMessage }));
      }
    }
  };
};

export const editComment = (commentId: number, commentData: TDataForUpdateCreateComment): App.ReduxAction => {
  return async (dispatch, getState) => {
    const state = getState();
    const articleId = commentData.postId;
    const parentId: number | null = commentData.parentId;
    const articleComments = state[articleId];

    if (!articleComments?.loading) {
      try {
        dispatch(commentsRequestStart(articleId));
        const comment = await updateComment(commentId, commentData);
        dispatch(loadingCommentSuccess({ comment, parentId, articleId, afterEditing: true }));
      } catch (e) {
        const errorMessage = e?.message ?? null;
        dispatch(commentsRequestFailure({ articleId, errorMessage }));
      }
    }
  };
};

export const removeComment = (commentId: number, parentId: number | null, articleId: number): App.ReduxAction => {
  return async (dispatch, getState) => {
    const state = getState();
    const articleComments = state[articleId];

    if (!articleComments?.loading) {
      try {
        dispatch(commentsRequestStart(articleId));
        await deleteComment(commentId);
        if (parentId) {
          dispatch(commentRemoveChildSuccess({ commentId, articleId, parentId }));
        } else {
          dispatch(commentRemoveFirstLevelSuccess({ commentId, articleId }));
        }
      } catch (e) {
        const errorMessage = e?.message ?? null;
        dispatch(commentsRequestFailure({ articleId, errorMessage }));
      }
    }
  };
};

const updatedDictionary = (
  oldDictionary: ICommentsDictionary,
  commentId: number,
  parentId: number | null,
): ICommentsDictionary => {
  const newDictionary = {
    ...oldDictionary,
  };

  delete newDictionary[commentId];

  // if has parent id, also remove child id from the parent's list of child id's
  if (parentId) {
    const parent = newDictionary[parentId];
    parent.childrenIds = newDictionary[parentId].childrenIds.filter((childId) => childId !== commentId);
    parent.commentsCount -= 1;
  }

  return newDictionary;
};

const comments = createReducer<IState>({}, initialState)
  .on(commentRemoveChildSuccess, (state, { commentId, articleId, parentId }) => {
    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        dictionary: updatedDictionary(state[articleId].dictionary, commentId, parentId),
        loading: false,
        error: null,
        total: state[articleId].total - 1,
      },
    };
  })
  .on(commentRemoveFirstLevelSuccess, (state, { commentId, articleId }) => {
    // remove comment ID from first level comments ids array
    const updatedCommentIds = state[articleId].firstLevelCommentIds.filter((id) => id !== commentId);

    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        dictionary: updatedDictionary(state[articleId].dictionary, commentId, null),
        firstLevelCommentIds: updatedCommentIds,
        totalFirstLevel: state[articleId].totalFirstLevel - 1,
        loading: false,
        error: null,
        total: state[articleId].total - 1,
      },
    };
  })
  .on(loadingCommentSuccess, (state, { comment, parentId, articleId, afterEditing = false }) => {
    let updatedDictionary = {};
    let updatedFirstLevelCommentIds;

    if (state[articleId]?.dictionary) {
      updatedDictionary = { ...state[articleId].dictionary };
      updatedFirstLevelCommentIds = [...state[articleId].firstLevelCommentIds];

      if (parentId) {
        // child arrived
        const parentComment = { ...state[articleId].dictionary[parentId] };

        parentComment.childrenIds = parentComment.childrenIds
          ? // if case updating already existing child
            uniq([...parentComment.childrenIds, comment.id])
          : [comment.id];

        if (parentComment.commentsCount < parentComment.childrenIds.length) {
          parentComment.commentsCount += 1;
        }

        updatedDictionary[parentId] = parentComment;
      } else {
        // in case updating already existing first lvl comment
        const existingCommentId: number | undefined = updatedFirstLevelCommentIds?.find(
          (commentId) => commentId === comment.id,
        );

        updatedFirstLevelCommentIds = existingCommentId
          ? [...state[articleId].firstLevelCommentIds]
          : [comment.id, ...state[articleId].firstLevelCommentIds];
      }
    } else {
      updatedFirstLevelCommentIds = [comment.id];
    }

    updatedDictionary[comment.id] = comment;

    const updatedTotalFirstLevel = () => {
      const oldFirstLevelTotal = state[articleId]?.totalFirstLevel;
      if (oldFirstLevelTotal && parentId) {
        // it is child comment
        return oldFirstLevelTotal;
      } else if (oldFirstLevelTotal && !parentId) {
        // already existing comment
        if (oldFirstLevelTotal === updatedFirstLevelCommentIds.length) {
          return oldFirstLevelTotal;
        }

        // it is new first level comment
        return oldFirstLevelTotal + 1;
      }
      // it is the first first level comment
      return 1;
    };

    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        dictionary: updatedDictionary,
        firstLevelCommentIds: updatedFirstLevelCommentIds,
        loading: false,
        totalFirstLevel: updatedTotalFirstLevel(),
        error: null,
        total: afterEditing ? state[articleId].total : (state[articleId].total || 0) + 1,
      },
    };
  })
  .on(loadingFirstLevelCommentsSuccess, (state, { comments, articleId }) => {
    const newCommentsDictionary = comments.items.reduce<ICommentsDictionary>((acc, comment) => {
      return {
        ...acc,
        [comment.id]: comment,
      };
    }, {});
    const newFirstLevelCommentsIds = comments.items.map((comment) => comment.id);

    let updatedDictionary;
    let updatedFirstlevelIds;

    if (state[articleId]?.dictionary) {
      updatedDictionary = merge(state[articleId].dictionary, newCommentsDictionary);

      const unNormalizedFirstLevelIds = [...state[articleId].firstLevelCommentIds, ...newFirstLevelCommentsIds];
      updatedFirstlevelIds = uniq(unNormalizedFirstLevelIds);
    } else {
      updatedDictionary = newCommentsDictionary;
      updatedFirstlevelIds = newFirstLevelCommentsIds;
    }

    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        dictionary: updatedDictionary,
        firstLevelCommentIds: updatedFirstlevelIds,
        pageIndex: comments.pageIndex,
        pageSize: comments.pageSize,
        totalFirstLevel: comments.total,
        loading: false,
        error: null,
      },
    };
  })
  .on(loadingChildCommentsSuccess, (state, { childComments, articleId, parentId }) => {
    const updatedParentComment = { ...state[articleId].dictionary[parentId] };
    updatedParentComment.childrenIds = childComments.items.map((comment) => comment.id);

    const childCommentsDictionary = childComments.items.reduce<ICommentsDictionary>((acc, comment) => {
      return {
        ...acc,
        [comment.id]: comment,
      };
    }, {});

    const updatedDictionary = merge(state[articleId].dictionary, childCommentsDictionary);
    updatedDictionary[parentId] = updatedParentComment;

    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        dictionary: updatedDictionary,
        firstLevelCommentIds: [...state[articleId].firstLevelCommentIds],
        loading: false,
        error: null,
      },
    };
  })
  .on(loadingCommentsTotalSuccess, (state, { total, articleId }) => {
    if (!total) return state;

    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        loading: false,
        error: null,
        total,
      },
    };
  })
  .on(commentsRequestStart, (state, articleId) => {
    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        loading: true,
        error: null,
      },
    };
  })
  .on(commentsRequestFailure, (state, { errorMessage, articleId }) => {
    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        loading: false,
        error: errorMessage,
      },
    };
  })
  .on(updateCommentLike, (state, { articleId, commentId, isLiked }) => {
    const currentComment = state[articleId].dictionary[commentId];

    return {
      ...state,
      [articleId]: {
        ...state[articleId],
        dictionary: {
          ...state[articleId].dictionary,
          [commentId]: {
            ...currentComment,
            likes: isLiked ? currentComment.likes - 1 : currentComment.likes + 1,
            isLiked: !isLiked,
          },
        },
      },
    };
  })
  .on(loadingCommentTotalsForListSuccess, (state, { totals }) => {
    const commentStates = totals.reduce((acc, total) => {
      if (!total.commentsCount) return acc;

      return {
        ...acc,
        [total.postId]: {
          total: total.commentsCount,
          loading: false,
          error: null,
        },
      };
    }, {});

    return {
      ...state,
      ...commentStates,
    };
  })
  .on(loadingCommentsTotalsForListFailure, (state, { articleIds, errorMessage }) => {
    const commentStates = articleIds.reduce((acc, id) => {
      return {
        ...acc,
        [id]: {
          loading: false,
          error: errorMessage,
        },
      };
    }, {});

    return {
      ...state,
      ...commentStates,
    };
  });

// eslint-disable-next-line import/no-default-export
export default comments;
